import { z } from 'zod';
/**
 * Form Names - All forms that track interactions in the system
 */
export const FORM_NAMES = {
    SESSION_FEEDBACK: 'session_feedback',
    REQUEST_SESSION: 'request_session',
    GENERAL_FEEDBACK: 'general_feedback',
};
/**
 * Form Actions - Standard actions tracked across all forms
 */
export const FORM_ACTIONS = {
    START: 'start',
    SUBMIT: 'submit',
    ABANDON: 'abandon',
};
// ============ GENERIC FORM SCHEMAS ============
/**
 * Generic Form Start Schema
 * Used by all forms when user begins typing
 * Uses passthrough() to allow additional context fields (like session_id)
 */
export const FormStartDataSchema = z.object({
    form_name: z.string(),
    form_action: z.literal(FORM_ACTIONS.START),
}).passthrough();
/**
 * Generic Form Abandon Schema
 * Used by all forms when user closes without submitting
 * Uses passthrough() to allow additional context fields (like session_id, length, etc.)
 */
export const FormAbandonDataSchema = z.object({
    form_name: z.string(),
    form_action: z.literal(FORM_ACTIONS.ABANDON),
}).passthrough();
// ============ FORM SUBMIT SCHEMAS ============
/**
 * Base Form Submit Schema
 * All form submit events share these fields
 * Lambda can validate against this first, then route by form_name
 */
export const FormSubmitBaseDataSchema = z.object({
    form_name: z.string(),
    form_action: z.literal(FORM_ACTIONS.SUBMIT),
    form_text: z.string(), // All forms require text content
}).passthrough();
/**
 * Session Feedback - Submit
 * Tracks when user submits feedback
 * Note: form_text comes from base schema (contains the feedback text)
 */
export const SessionFeedbackSubmitDataSchema = FormSubmitBaseDataSchema.extend({
    form_name: z.literal(FORM_NAMES.SESSION_FEEDBACK),
    session_id: z.string(),
    session_title: z.string().optional(),
    session_category: z.string().optional(),
    audio_file: z.string().optional(),
    has_audio: z.boolean(),
});
/**
 * Request Session - Submit
 * Tracks when user submits session request
 * Note: form_text comes from base schema (contains the request text)
 */
export const RequestSessionSubmitDataSchema = FormSubmitBaseDataSchema.extend({
    form_name: z.literal(FORM_NAMES.REQUEST_SESSION),
    request_length: z.number().optional(),
});
/**
 * General Feedback - Submit
 * Tracks when user submits general site feedback
 * Note: form_text comes from base schema (contains the feedback text)
 */
export const GeneralFeedbackSubmitDataSchema = FormSubmitBaseDataSchema.extend({
    form_name: z.literal(FORM_NAMES.GENERAL_FEEDBACK),
    feedback_type: z.enum(['bug', 'feature', 'general']).optional(),
    page_path: z.string().optional(),
});
// ============ UNION TYPES ============
/**
 * Union of all form submit data types (the specific ones)
 */
export const FormSubmitDataSchema = z.union([
    SessionFeedbackSubmitDataSchema,
    RequestSessionSubmitDataSchema,
    GeneralFeedbackSubmitDataSchema,
]);
/**
 * Union of all form data types (generic start/abandon + specific submits)
 */
export const AllFormDataSchema = z.union([
    FormStartDataSchema,
    FormAbandonDataSchema,
    FormSubmitDataSchema,
]);
